import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal, ModalContent, ModalHeader, addToast } from '@heroui/react';
import { Plane, Wrench } from 'lucide-react';
import useBreakpoint from '../../calendar-common/hooks/useBreakpoint.js';
import CalendarApi from '../../../api/CalendarApi.js';
import ResourcesApi from '../../../api/resourcesApi.js';
import EventFormBody from './EventFormBody.js';
import { isSubmittedMyEvent } from '../utils/isSubmittedMyEvent.js';

/**
 * CreateEventModal — thin wrapper around `<EventFormBody mode="create" />`.
 *
 * The shared form lives in `EventFormBody`; this component owns only:
 *   - the HeroUI `<Modal>` chrome
 *   - the header (icon + title) which differs by event type at runtime
 *   - the create-mode `onSave` callback (routing between `createEvent` /
 *     `createMyEvent` via `isSubmittedMyEvent` for ownership)
 *
 * Refactored from the legacy 1300-line monolith. The persistent-mount /
 * stale-`formOrgUnique` fix from sub-epic `dc71b057` lives inside
 * `EventFormBody`'s init `useEffect` (imperative `store.getState()` keyed on
 * the page `orgUnique` prop).
 */
function CreateEventModal({
	isOpen,
	onClose,
	initialDayTs,
	// Optional: the exact instant (unix seconds) the user clicked, for views that
	// have time-slot granularity. Month has none and never passes it, so its
	// wall-clock default is untouched.
	initialStartTs,
	orgUnique,
	onCreated,
	// Waiting List flow — when the user clicks "Add to Waiting List" on a
	// view-modal event, we open the SAME create form pre-filled with that
	// event's data. `initialFormState` shape matches `EventFormBody`'s
	// edit-mode hydration; `forceAddToWaiting=true` flips the payload's
	// `addToWaiting` to true via `buildEventPayload` opts.
	initialFormState,
	forceAddToWaiting = false,
	headerLabel: headerLabelOverride
}) {
	const resourcesMap = useSelector(state => state?.resources);
	const currentUserId = useSelector(
		state => state?.user?.id ?? state?.user?.userId ?? state?.user?._userID ?? null
	);

	// Track the event type selected in EventFormBody so the header stays in sync.
	// Seeded to 'air'; reset to 'air' by EventFormBody's onEventTypeChange callback
	// whenever the modal opens/resets (init effect fires and calls the callback).
	const [activeEventType, setActiveEventType] = useState('air');

	const headerLabel =
		headerLabelOverride ||
		(forceAddToWaiting
			? 'New Waiting List Event'
			: activeEventType === 'maintenance'
				? 'New Maintenance Event'
				: 'New Regular Event');
	const headerIcon =
		activeEventType === 'maintenance' ? (
			<Wrench className="w-5 h-5" />
		) : (
			<Plane className="w-5 h-5" />
		);

	const handleSave = useCallback(
		async (payload, formOrgUnique) => {
			if (payload.type === 'maintenance') {
				try {
					const res = await ResourcesApi.addMaintenance(
						payload.resources[0],
						{
							startTime: payload.startTime,
							finishTime: payload.finishTime,
							comments: payload.comments,
							// Resource location tz (NOT filter/browser tz) — required for
							// `formatMaintenanceDate` to produce a wall-clock the backend
							// reinterprets correctly. Falls back to `payload.timezone` for
							// pre-Rem17 callers that haven't been migrated.
							timezoneId: payload.locationTimezoneId || payload.timezone
						},
						formOrgUnique
					);
					const body = res?.data ?? res;
					if (body?.success === false) {
						const msg = (body.error && body.error[0]) || 'Create failed';
						addToast({ title: 'Create failed', description: msg, color: 'danger' });
						return { ok: false };
					}
					addToast({ title: 'Event created', color: 'success' });
					onClose?.();
					onCreated?.();
					return { ok: true };
				} catch (err) {
					const msg = err?.response?.data?.error?.[0] || err?.message || 'Create failed';
					addToast({ title: 'Create failed', description: msg, color: 'danger' });
					return { ok: false };
				}
			}

			// Ownership routing: own events go to `createMyEvent` (only needs
			// CALENDAR_MANAGE_OWN), anothers go to `createEvent` (requires
			// CALENDAR_MANAGE_ANOTHERS). Mirrors legacy create flow.
			const isOwn = isSubmittedMyEvent(payload, currentUserId, resourcesMap);
			try {
				const res = isOwn
					? await CalendarApi.createMyEvent(payload, formOrgUnique)
					: await CalendarApi.createEvent(payload, formOrgUnique);
				const body = res?.data ?? res;
				if (body?.success === false) {
					// HW-1026/1027 — BACKUP RESERVATION: on a booked-out slot the
					// server still CREATES the event as a waiting-list (backup)
					// reservation and answers success:false + error("…already booked…")
					// + info("The event has been created"). That's a success with a
					// caveat, not a failure — say so, close, refetch (backups render
					// with the striped "Waiting" skin; the waiting filter defaults on).
					const createdAsBackup =
						Array.isArray(body.info) && body.info.some(m => /has been created/i.test(String(m)));
					if (createdAsBackup) {
						const conflict =
							(body.error && body.error[0]) || 'The requested slot is already booked';
						addToast({
							title: 'Added as BACKUP reservation',
							description: `${conflict}. The event was placed on the waiting list — it shows with the striped "Waiting" style until the slot frees up.`,
							color: 'warning',
							timeout: 8000
						});
						onClose?.();
						onCreated?.();
						return { ok: true, backup: true };
					}
					const msg = (body.error && body.error[0]) || 'Create failed';
					addToast({ title: 'Create failed', description: msg, color: 'danger' });
					return { ok: false };
				}
				addToast({ title: 'Event created', color: 'success' });
				onClose?.();
				onCreated?.();
				return { ok: true };
			} catch (err) {
				const msg = err?.response?.data?.error?.[0] || err?.message || 'Create failed';
				addToast({ title: 'Create failed', description: msg, color: 'danger' });
				return { ok: false };
			}
		},
		[currentUserId, resourcesMap, onClose, onCreated]
	);

	const { phone: isNarrow } = useBreakpoint();

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			size={isNarrow ? 'md' : '3xl'}
			placement={isNarrow ? 'bottom' : 'center'}
			scrollBehavior="inside"
			classNames={{
				base: isNarrow
					? 'shadow-cal-elev-3 max-h-[90vh] m-0 rounded-b-none rounded-t-lg'
					: 'shadow-cal-elev-3'
			}}
			motionProps={
				isNarrow
					? {
							variants: {
								enter: { y: 0, opacity: 1, transition: { duration: 0.2, ease: 'easeOut' } },
								exit: { y: 40, opacity: 0, transition: { duration: 0.15, ease: 'easeIn' } }
							}
						}
					: undefined
			}
		>
			<ModalContent>
				<ModalHeader className="flex items-center gap-3 border-b border-default-200 pr-10">
					<span style={{ color: 'hsl(var(--heroui-primary))' }}>{headerIcon}</span>
					<span
						className="text-lg font-bold flex-1"
						style={{ color: 'hsl(var(--heroui-foreground))' }}
					>
						{headerLabel}
					</span>
				</ModalHeader>
				<EventFormBody
					mode="create"
					isOpen={isOpen}
					orgUnique={orgUnique}
					initialDayTs={initialDayTs}
					initialStartTs={initialStartTs}
					initialFormState={initialFormState}
					forceAddToWaiting={forceAddToWaiting}
					onSave={handleSave}
					onCancel={onClose}
					onEventTypeChange={setActiveEventType}
				/>
			</ModalContent>
		</Modal>
	);
}

// Keep `Wrench` importable from this module for any external callers that
// may have grabbed it indirectly (defensive — current source doesn't, but
// the previous file re-exported icons by virtue of shape).
export { Wrench };

export default CreateEventModal;
